import {useCallback, useState} from "react";
import {useDropzone, FileRejection} from "react-dropzone";
import {PrimaryButton} from "../../../../../components/general/PrimaryButton";
import {XMarkIcon} from "@heroicons/react/24/outline";
import type {DocumentDirection} from "../../../../../generated/enums.ts";

interface DocumentDropzoneProps {
    label: string;
    documentDirection: DocumentDirection;
    supportedFileTypes: Record<string, string[]>;
    supportedFileExtensions: string[];
    maxFiles: number;
    maxSize: number;
    onUpload: (files: File[], documentDirection: DocumentDirection) => Promise<void>;
    files: File[];
    setFiles: (files: File[]) => void;
}

const handleErrorOnFileSelection = (supportedFileExtensions: string[]) => ({
    "file-invalid-type": (rejection: FileRejection) => {
        return `Bestandstype niet ondersteund. Alleen bestandstypen ${supportedFileExtensions.join(', ')} worden ondersteund.`;
    }
});

export function DocumentDropzone({
    label,
    documentDirection,
    supportedFileTypes,
    supportedFileExtensions,
    maxFiles,
    maxSize,
    onUpload,
    files,
    setFiles
}: DocumentDropzoneProps) {
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [filesAreBeingUploaded, setFilesAreBeingUploaded] = useState<boolean>(false);

    const onDropAccepted = useCallback((acceptedFiles: File[]) => {
        setFiles(acceptedFiles);
    }, [setFiles]);

    const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
        const errors = fileRejections.map(
            (rejection) => {
                const handler = handleErrorOnFileSelection(supportedFileExtensions)[rejection.errors[0].code];
                const errorMessage = handler ? handler(rejection) : rejection.errors[0].message;
                return `${rejection.file.name}: ${errorMessage}`;
            }
        );
        setUploadError(errors.join('\n'));
    }, [supportedFileExtensions]);

    const dropzone = useDropzone({
        onDropAccepted,
        onDropRejected,
        maxFiles,
        maxSize,
        accept: supportedFileTypes,
    });

    async function handleUpload() {
        setFilesAreBeingUploaded(true);
        await onUpload(files, documentDirection);
        setFilesAreBeingUploaded(false);
        setFiles([]);
    }

    return (
        <div>
            <div className="block mb-2 text-lg font-semibold">{label}</div>
            <div {...dropzone.getRootProps({className: `flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600 ${dropzone.isDragActive ? 'bg-primary text-white' : ''}`})}>
                <input {...dropzone.getInputProps()} />
                {files.length === 0 && (
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        {dropzone.isDragActive ? (
                            <p className="my-2 text-lg text-white">Drop it like it's hot!</p>
                        ) : (
                            <>
                                <svg className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400"
                                     aria-hidden="true"
                                     xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                                    <path stroke="currentColor" strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="2"
                                          d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                                </svg>
                                <p className="mb-2 text-sm text-gray-500 dark:text-gray-400"><span
                                    className="font-semibold">Klik om te uploaden</span> of sleep bestanden hierheen</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{supportedFileExtensions.join(', ').toUpperCase()}</p>
                            </>
                        )}
                    </div>
                )}
            </div>
            {uploadError && <div
                className="border border-red-300 bg-red-100 rounded-lg p-4 my-2 flex items-center justify-between cursor-pointer">
                <span>{uploadError}</span>
                <XMarkIcon className="h-6 w-6 text-red-400" onClick={() => setUploadError(null)}/>
            </div>}
            {files.length > 0 && (
                <div className="file-list">
                    <ul>
                        {files.map((file) => (
                            <li key={file.name}
                                className="border border-gray-300 rounded-lg p-4 my-2 flex items-center justify-between cursor-pointer">
                                <div><span>{file.name}</span> <span
                                    className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</span>
                                </div>
                                <div className="h-8 w-8 hover:bg-gray-100 rounded-full p-1">
                                    <XMarkIcon onClick={() => {
                                        setFiles(files.filter(f => f.name !== file.name))
                                    }}/>
                                </div>
                            </li>
                        ))}
                    </ul>
                    <PrimaryButton
                        title={filesAreBeingUploaded ? 'Uploaden...' : 'Uploaden'}
                        onClick={handleUpload}
                        disabled={filesAreBeingUploaded}
                    />
                </div>
            )}
        </div>
    );
} 